import { supabase } from '../lib/supabase';

const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    acc[camelKey] = toCamel(obj?.[key]);
    return acc;
  }, {});
};

const toSnake = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnake);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (l) => `_${l?.toLowerCase()}`);
    acc[snakeKey] = toSnake(obj?.[key]);
    return acc;
  }, {});
};

export const providerCategoryService = {
  async getAll(companyId) {
    try {
      let query = supabase?.from('provider_categories')?.select('*')?.eq('active', true)?.order('sort_order');
      if (companyId) {
        query = supabase?.from('provider_categories')?.select('*')?.or(`company_id.eq.${companyId},company_id.is.null`)?.eq('active', true)?.order('sort_order');
      }
      const { data, error } = await query;
      if (error) { console.error('Error fetching provider categories:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch provider categories:', err);
      return [];
    }
  },

  async create(categoryData) {
    try {
      const { data, error } = await supabase?.from('provider_categories')?.insert(toSnake(categoryData))?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to create provider category:', err);
      throw err;
    }
  },

  async update(id, updateData) {
    try {
      const { data, error } = await supabase?.from('provider_categories')?.update(toSnake(updateData))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to update provider category:', err);
      throw err;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('provider_categories')?.update({ active: false })?.eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to delete provider category:', err);
      throw err;
    }
  }
};

export const providerService = {
  async getAll(companyId) {
    try {
      let query = supabase?.from('providers')?.select('*, category:provider_categories(id, name)')?.eq('active', true)?.order('name');
      if (companyId) {
        query = supabase?.from('providers')?.select('*, category:provider_categories(id, name)')?.eq('company_id', companyId)?.eq('active', true)?.order('name');
      }
      const { data, error } = await query;
      if (error) { console.error('Error fetching providers:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('providers')?.select('*, category:provider_categories(id, name)')?.eq('id', id)?.maybeSingle();
      if (error) { console.error('Error fetching provider:', error); return null; }
      return data ? toCamel(data) : null;
    } catch (err) {
      console.error('Failed to fetch provider:', err);
      return null;
    }
  },

  async create(providerData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = toSnake({ ...providerData });
      const { data, error } = await supabase?.from('providers')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to create provider:', err);
      throw err;
    }
  },

  async update(id, updateData) {
    try {
      const { data, error } = await supabase?.from('providers')?.update(toSnake(updateData))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to update provider:', err);
      throw err;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('providers')?.update({ active: false })?.eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to delete provider:', err);
      throw err;
    }
  }
};

export const providerQuoteService = {
  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase?.from('provider_quotes')?.select('*, provider:providers(id, name, phone, whatsapp), items:provider_quote_items(*)')?.eq('work_order_id', workOrderId)?.order('created_at', { ascending: false });
      if (error) { console.error('Error fetching provider quotes:', error); return []; }
      return (data || [])?.map(toCamel);
    } catch (err) {
      console.error('Failed to fetch provider quotes:', err);
      return [];
    }
  },

  async create(quoteData, items = []) {
    try {
      const { data: quote, error: quoteErr } = await supabase?.from('provider_quotes')?.insert(toSnake(quoteData))?.select()?.single();
      if (quoteErr) throw quoteErr;

      if (items?.length > 0) {
        const itemsPayload = items?.map(item => toSnake({ ...item, providerQuoteId: quote?.id }));
        const { error: itemsErr } = await supabase?.from('provider_quote_items')?.insert(itemsPayload);
        if (itemsErr) console.error('Error inserting provider quote items:', itemsErr);
      }

      return toCamel(quote);
    } catch (err) {
      console.error('Failed to create provider quote:', err);
      throw err;
    }
  },

  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase?.from('provider_quotes')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('Failed to update provider quote status:', err);
      throw err;
    }
  }
};
